const THUMBNAIL_ACCEPTED_TYPES = ["image/jpeg", "image/png"];
const THUMBNAIL_ACCEPTED_EXTENSIONS = [".jpg", ".jpeg", ".png"];
const THUMBNAIL_MAX_SIZE_BYTES = 2 * 1024 * 1024;
const THUMBNAIL_MAX_SIZE_LABEL = "2MB";
const THUMBNAIL_ACCEPT_ATTR = ".jpg,.jpeg,.png";

export { THUMBNAIL_ACCEPT_ATTR };

interface ThumbnailValidationResult {
  valid: boolean;
  error?: string;
}

export function validateThumbnailFile(
  file: File,
): Promise<ThumbnailValidationResult> {
  return new Promise((resolve) => {
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
    if (
      !THUMBNAIL_ACCEPTED_EXTENSIONS.includes(ext) &&
      !THUMBNAIL_ACCEPTED_TYPES.includes(file.type)
    ) {
      resolve({ valid: false, error: "Thumbnail must be a JPG or PNG file." });
      return;
    }

    if (file.size > THUMBNAIL_MAX_SIZE_BYTES) {
      resolve({
        valid: false,
        error: `Thumbnail must be under ${THUMBNAIL_MAX_SIZE_LABEL}.`,
      });
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.naturalWidth !== img.naturalHeight) {
        resolve({
          valid: false,
          error: `Thumbnail must be square. Selected image is ${img.naturalWidth}×${img.naturalHeight}px.`,
        });
      } else {
        resolve({ valid: true });
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ valid: false, error: "Could not read image file." });
    };

    img.src = url;
  });
}

export const THUMBNAIL_HINT = "Square image, JPG or PNG, max 2MB";

export const AUDIO_HINT = "MP3, WAV, AAC, or OGG";
export const AUDIO_ACCEPT_ATTR = ".mp3,.wav,.aac,.ogg,.m4a";

// EVO-73: the hint follows the selected orientation instead of always "Horizontal".
export const VIDEO_HINT = "MP4, MOV, or WebM";
export function videoHint(ratio?: "LANDSCAPE" | "PORTRAIT" | null): string {
  if (ratio === "PORTRAIT") return `Vertical (portrait) orientation · ${VIDEO_HINT}`;
  if (ratio === "LANDSCAPE") return `Horizontal (landscape) orientation · ${VIDEO_HINT}`;
  return `Select an orientation, then upload · ${VIDEO_HINT}`;
}
export const VIDEO_ACCEPT_ATTR = ".mp4,.mov,.webm";

const AUDIO_ACCEPTED_EXTENSIONS = AUDIO_ACCEPT_ATTR.split(",");
// shortcut: 500MB cap is a sane ceiling for long-form WAV; tune if product sets one.
const AUDIO_MAX_SIZE_BYTES = 500 * 1024 * 1024;

export function validateAudioFile(file: File): ThumbnailValidationResult {
  const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
  if (!AUDIO_ACCEPTED_EXTENSIONS.includes(ext)) {
    return { valid: false, error: `Audio must be one of: ${AUDIO_HINT}.` };
  }
  if (file.size > AUDIO_MAX_SIZE_BYTES) {
    return { valid: false, error: "Audio file must be under 500MB." };
  }
  return { valid: true };
}
